import type { ISettingValueMaster } from "@bbj-types/setting";
import { TextField } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { AutocompleteSettings } from "@styleguide";
import { useEffect } from "react";
import { type Control, Controller, type UseFormSetValue, useWatch } from "react-hook-form";
import type { IModalActionForm, IModalActionResolver } from "../logics/types";

export const isOptionDisabled = (option: ISettingValueMaster, type: ISettingValueMaster | null) => {
  const enabledContactChannels = type?.metadata?.enabledContactChannels;
  if (!enabledContactChannels) {
    return false;
  }

  // Check if the current type is included in the enabled contact channels
  return !enabledContactChannels.includes(option.value);
};

export interface IModalActionFormContactChannelProps {
  control: Control<IModalActionForm, void, IModalActionResolver>;
  setValue: UseFormSetValue<IModalActionForm>;
}

export const ModalActionFormContactChannel = ({ control, setValue }: IModalActionFormContactChannelProps) => {
  const { settingsV2, user } = useGlobalContext();
  const [type, contactChannel] = useWatch({ control, name: ["type", "contactChannel"] });

  useEffect(() => {
    if (
      contactChannel &&
      type?.metadata?.enabledContactChannels &&
      !type.metadata.enabledContactChannels.includes(contactChannel.value)
    ) {
      setValue("contactChannel", null);
    }
  }, [type, contactChannel, setValue]);

  return (
    <Controller
      control={control}
      name="contactChannel"
      render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
        <AutocompleteSettings
          value={value}
          onChange={(_, value) => onChange(value)}
          onBlur={onBlur}
          options={settingsV2.actionChannel?.values ?? []}
          size="small"
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={(options) => {
            const filteredOptions: ISettingValueMaster[] = [];
            for (const option of options) {
              if (!isOptionDisabled(option, type)) {
                filteredOptions.push(option);
              }
            }
            return filteredOptions;
          }}
          showIcons
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Canal${user.organisation.features.action.requiredContactDetails?.includes(type?.value ?? "") ? " *" : ""}`}
              inputRef={ref}
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />
      )}
    />
  );
};
